import PropTypes from 'prop-types'
import React from 'react'

import { HorizontalCheckboxQuestionRenderer } from '../../../ui/HorizontalCheckboxQuestionRenderer'
import { hasText } from '../../../utils/helpers'
import ZentakeFormContext from '../ZentakeFormContext'

function HorizontalCheckboxQuestionList({
  question,
  isOfficeUse,
  response,
  sessions,
  mode,
}) {
  const [details, setDetails] = React.useState({})

  const formContext = React.useContext(ZentakeFormContext)
  const readonly = formContext.isQuestionReadonly(question)
  const willShowNotAllowedCursor =
    formContext.willShowNotAllowedCursor(question)

  const isDirty = formContext.isQuestionDirty(question)
  const isValid = formContext.isQuestionValid(question)
  const questionError = formContext.getQuestionError(question)

  const [hasError, setHasError] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(
    'This field is required'
  )

  const answer = formContext.getAnswer(question)
  const choices =
    answer && answer.answer_raw ? answer.answer_raw.split(',') : []

  const officeUsePreview = mode === 'preview' && question.is_office_use_only

  React.useEffect(() => {
    setHasError(() => {
      if (hasText(questionError)) return true
      if (isDirty && question?.is_required && !isValid) return true
      return false
    })
    setErrorMessage(() => {
      if (questionError && typeof questionError === 'string') {
        return questionError
      }
      return 'This field is required'
    })
  }, [isDirty, question?.is_required, isValid, questionError])

  function getValue(choiceId) {
    return choices.some((c) => c === choiceId.toString())
  }

  const getChoiceDetails = React.useCallback(
    (choiceId) => {
      if (!details?.[choiceId]) {
        return (
          answer?.answer_choices?.find(
            // eslint-disable-next-line
            (ch) => ch?.question_choice?.id == choiceId
          )?.text_details || ''
        )
      }
      return details?.[choiceId] ?? ''
    },
    [answer, details]
  )

  function handleChange(choiceId, value, textDetails) {
    setDetails((prev) => {
      if (value === true && hasText(textDetails)) {
        return {
          ...(prev ?? {}),
          [choiceId]: textDetails,
        }
      }

      if (value === false) {
        const { [choiceId]: removed, ...next } = prev

        return next
      }
      return prev
    })
    formContext.onAnswerChanged(question, { value, choiceId, textDetails })
  }

  const updateLocalDetails = React.useCallback((choiceId, value) => {
    setDetails((prevDetails) => ({
      ...prevDetails,
      [choiceId]: value,
    }))
  }, [])

  return (
    <HorizontalCheckboxQuestionRenderer
      details={details}
      errorMessage={errorMessage}
      getChoiceDetails={getChoiceDetails}
      getValue={getValue}
      hasError={hasError}
      isOfficeUse={isOfficeUse || officeUsePreview}
      isRequired={question.is_required}
      label={question.text}
      onChange={handleChange}
      onUpdateDetails={updateLocalDetails}
      question={question}
      questionChoices={question.question_choices}
      questionId={question.id}
      readonly={readonly}
      response={response}
      sessions={sessions}
      totalAnswerChoices={choices.length}
      willShowNotAllowedCursor={willShowNotAllowedCursor}
    />
  )
}

HorizontalCheckboxQuestionList.propTypes = {
  question: PropTypes.object.isRequired,
}

export default React.memo(HorizontalCheckboxQuestionList)
